import React from 'react';
import styled from 'styled-components';
import { ProjectContainer, ProjectTitle, ProjectInfo } from './styles';
import { useLanguage } from '../../contexts/LanguageContext';

const ProjectDescription = styled.div`
  margin-bottom: 30px;
  line-height: 24px;
`;

const ProjectWrapper = styled.div`
  margin-bottom: 40px;
  
  h4 {
    font-size: 20px;
    color: #333;
    margin: 0 0 20px 0;
    font-weight: 600;
  }
  
  h3 {
    font-size: 18px;
    color: #333;
    margin: 0 0 15px 0;
    font-weight: 600;
  }
  
  p {
    line-height: 24px;
    margin-bottom: 15px;
    
    &.text-muted {
      color: #6c757d;
    }
    
    a {
      color: #F34452;
      text-decoration: none;
      
      &:hover {
        text-decoration: underline;
      }
    }
  }
  
  img {
    width: 100%;
    max-width: 600px;
    height: auto;
    margin-bottom: 20px;
    border: 1px solid #eee;
    
    &.projects-responsive {
      max-width: 400px;
    }
  }
  
  .timeline {
    .row {
      .col-md-12 {
        .exp {
          margin-bottom: 30px;
          padding: 20px;
          border-left: 3px solid #F34452;
          background: #f8f9fa;
          
          .hgroup {
            margin-bottom: 15px;
            
            h4 {
              margin: 0;
              color: #333;
              font-size: 18px;
            }
          }
          
          ul {
            list-style: none;
            padding: 0;
            
            li {
              margin-bottom: 10px;
              
              a {
                color: #F34452;
                text-decoration: none;
                
                &:hover {
                  text-decoration: underline;
                }
              }
            }
          }
        }
      }
    }
  }
  
  .pic {
    .col-md-3, .col-xs-6 {
      margin-bottom: 20px;
      text-align: center;
      
      div {
        margin-bottom: 10px;
      }
      
      a {
        display: block;
        
        img {
          transition: transform 0.3s;
          
          &:hover {
            transform: scale(1.05);
          }
        }
      }
    }
  }
  
  .btn {
    background: #F34452;
    color: #FFF;
    padding: 8px 20px;
    text-decoration: none;
    font-weight: 600;
    border-radius: 3px;
    transition: all 0.3s;
    display: inline-block;
    border: none;
    cursor: pointer;
    font-size: 14px;
    
    &:hover {
      background: #d63946;
    }
  }
  
  ul {
    &.list-inline {
      list-style: none;
      padding: 0;
      display: flex;
      gap: 20px;
      flex-wrap: wrap;
      
      li {
        b {
          color: #333;
        }
      }
    }
    
    li {
      margin-bottom: 8px;
    }
  }
  
  .closeBtn {
    text-align: center;
    margin-top: 30px;
    
    button {
      background: #007bff;
      color: #FFF;
      border: none;
      padding: 12px 25px;
      border-radius: 3px;
      cursor: pointer;
      font-weight: 600;
      transition: all 0.3s;
      
      &:hover {
        background: #0056b3;
      }
      
      i {
        margin-right: 8px;
      }
    }
  }
`;

interface ProjectSeProps {
}

const ProjectSe: React.FC<ProjectSeProps> = () => {
  const { t } = useLanguage();
  
  return (
    <ProjectContainer>
      <ProjectTitle>
        <h2>{t('se.title')}</h2>
        <p className="tags">{t('se.role')}</p>
      </ProjectTitle>
      
      <ProjectDescription>
        {t('se.description')}
      </ProjectDescription>
      
      <ProjectInfo>
        <p><b>{t('se.roleLabel')}</b> {t('se.myRole')}</p>
        <p><b>{t('se.skillsLabel')}</b> {t('se.skillsUtilized')}</p>
      </ProjectInfo>

      <ProjectWrapper>
        <h4>{t('se.swDevProcess')}</h4>
        <div className="timeline">
          <div className="row">
            <div className="col-md-12">
              <div className="exp">
                <div className="hgroup">
                  <h4>{t('se.feasibility')}</h4>
                </div>
                <p>{t('se.feasibilityDesc')}</p>
                <p>{t('se.feasibilityDocs')} <a href="documents/FeasibilityStudy.pdf" target="_blank" rel="noopener noreferrer">Feasibility Study</a></p>
              </div>
              <div className="exp">
                <div className="hgroup">
                  <h4>{t('se.requirements')}</h4>
                </div>
                <p>{t('se.requirementsDesc')}</p>
                <p className="text-muted">{t('se.requirementsNote')}</p>
                <p><b>{t('se.documentsLabel')}</b> <a href="documents/requirement.pdf" target="_blank" rel="noopener noreferrer">Requirement Analysis</a></p>
                <a href="http://op05ah.axshare.com/" target="_blank" rel="noopener noreferrer" className="btn">{t('se.seeDemo')}</a>
              </div>
              <div className="exp">
                <div className="hgroup">
                  <h4>{t('se.systemDesign')}</h4>
                </div>
                <ul><b>{t('se.documentsLabel')}</b>
                  <li><a href="documents/SystemDesign.pdf" target="_blank" rel="noopener noreferrer">System Design</a></li>
                  <li><a href="documents/DataModel.pdf" target="_blank" rel="noopener noreferrer">Data Model</a></li>
                  <li><a href="documents/UseCasesandUserWorkflow.pdf" target="_blank" rel="noopener noreferrer">Use Cases and User Workflow</a></li>
                </ul>
              </div>
              <div className="exp">
                <div className="hgroup">
                  <h4>{t('se.implementation')}</h4>
                </div>
                <p className="text-muted">{t('se.implementationDesc')}</p>
                <p><b>{t('se.documentsLabel')}</b> <a href="documents/FinalReport.pdf" target="_blank" rel="noopener noreferrer">Final Report</a></p>
              </div>
              <div className="exp">
                <div className="hgroup">
                  <h4>{t('se.launchApp')}</h4>
                </div>
              </div>
            </div>
          </div>
        </div>
      </ProjectWrapper>

      <ProjectWrapper>
        <div className="row pic">
          <div className="col-md-3 col-xs-6">
            <div>
              <h3>{t('se.systemDesignTitle')}</h3>
            </div>
            <a href="/images/projects/SE01-min.webp">
              <img loading="lazy" decoding="async" className="img-responsive" src="/images/projects/SE01-min.webp" alt="System Design"/>
            </a>
          </div>
          <div className="col-md-3 col-xs-6">
            <div>
              <h3>{t('se.dataModel')}</h3>
            </div>
            <img loading="lazy" decoding="async" className="projects-responsive" src="/images/projects/SE02-min.webp" alt="Data Model" />
          </div>
          <div className="col-md-3 col-xs-6">
            <div>
              <h3>{t('se.useCases')}</h3>
            </div>
            <img loading="lazy" decoding="async" className="projects-responsive" src="/images/projects/SE03-min.webp" alt="Use Cases" />
          </div>
          <div className="col-md-3 col-xs-6">
            <div>
              <h3>{t('se.prototypes')}</h3>
            </div>
            <img loading="lazy" decoding="async" className="projects-responsive" src="/images/projects/SE04-min.webp" alt="Prototypes" />
          </div>
        </div>
      </ProjectWrapper>

      <ProjectWrapper>
        <ul className="list-inline">
          <li><b>{t('se.dateLabel')}</b> {t('se.dateRange')}</li>
          <li><b>{t('se.clientLabel')}</b> {t('se.client')}</li>
          <li><b>{t('se.teamMembersLabel')}</b> {t('se.teamMembers')}</li>
        </ul>
      </ProjectWrapper>
    </ProjectContainer>
  );
};

export default ProjectSe;